import { create } from 'zustand';
import { Task, TaskFilter, Toast, CreateTaskInput } from '../types';
import { taskApi } from '../services/api';

interface TaskState {
  tasks: Task[];
  filter: TaskFilter;
  loading: boolean;
  error: string | null;
  selectedTask: Task | null;
  toasts: Toast[];
  
  // Actions
  fetchTasks: () => Promise<void>;
  createTask: (title: string, description?: string, createdBy?: string) => Promise<void>;
  updateTask: (id: number, updates: { title?: string; description?: string; completed?: boolean }) => Promise<void>;
  deleteTask: (id: number) => Promise<void>;
  toggleTask: (id: number) => Promise<void>;
  setFilter: (filter: TaskFilter) => void;
  setSelectedTask: (task: Task | null) => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  uploadAttachments: (taskId: number, files: File[]) => Promise<void>;
  deleteAttachment: (taskId: number, attachmentId: number) => Promise<void>;
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  filter: 'active', // Default to active tasks
  loading: false,
  error: null,
  selectedTask: null,
  toasts: [],

  fetchTasks: async () => {
    set({ loading: true, error: null });
    try {
      const tasks = await taskApi.getTasks();
      set({ tasks, loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      get().addToast({ type: 'error', message: error.message });
    }
  },

  createTask: async (title: string, description?: string, createdBy?: string) => {
    set({ loading: true, error: null });
    try {
      // Prepare request body - only include fields with values
      const requestBody: CreateTaskInput = { title };
      if (description && description.trim()) {
        requestBody.description = description;
      }
      if (createdBy && createdBy.trim()) {
        requestBody.created_by = createdBy;
      }

      const newTask = await taskApi.createTask(requestBody);
      set(state => ({ 
        tasks: [newTask, ...state.tasks], 
        loading: false 
      }));
      get().addToast({ type: 'success', message: 'Görev oluşturuldu' });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      get().addToast({ type: 'error', message: error.message });
      throw error;
    }
  },

  updateTask: async (id: number, updates) => {
    set({ loading: true, error: null });
    try {
      const updatedTask = await taskApi.updateTask(id, updates);
      set(state => ({
        tasks: state.tasks.map(t => t.id === id ? updatedTask : t),
        selectedTask: state.selectedTask?.id === id ? updatedTask : state.selectedTask,
        loading: false
      }));
      get().addToast({ type: 'success', message: 'Görev güncellendi' });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      get().addToast({ type: 'error', message: error.message });
      throw error;
    }
  },

  deleteTask: async (id: number) => {
    set({ loading: true, error: null });
    try {
      await taskApi.deleteTask(id);
      set(state => ({
        tasks: state.tasks.filter(t => t.id !== id),
        selectedTask: state.selectedTask?.id === id ? null : state.selectedTask,
        loading: false
      }));
      get().addToast({ type: 'success', message: 'Görev silindi' });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      get().addToast({ type: 'error', message: error.message });
      throw error;
    }
  },

  toggleTask: async (id: number) => {
    try {
      const updatedTask = await taskApi.toggleTask(id);
      set(state => ({
        tasks: state.tasks.map(t => t.id === id ? updatedTask : t),
        selectedTask: state.selectedTask?.id === id ? updatedTask : state.selectedTask,
      }));
    } catch (error: any) {
      get().addToast({ type: 'error', message: error.message });
      throw error;
    }
  },

  setFilter: (filter: TaskFilter) => {
    set({ filter });
  },

  setSelectedTask: (task: Task | null) => {
    set({ selectedTask: task });
  },

  addToast: (toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(7);
    const newToast: Toast = { ...toast, id, duration: toast.duration || 3000 };
    set(state => ({ toasts: [...state.toasts, newToast] }));
    
    // Auto remove after duration
    setTimeout(() => {
      get().removeToast(id);
    }, newToast.duration);
  },

  removeToast: (id: string) => {
    set(state => ({ toasts: state.toasts.filter(t => t.id !== id) }));
  },

  uploadAttachments: async (taskId: number, files: File[]) => {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/tasks/${taskId}/attachments`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Dosya yüklenemedi');
      }

      const result = await response.json();
      const attachments = result.data;

      // Update task with new attachments
      set(state => ({
        tasks: state.tasks.map(t => 
          t.id === taskId 
            ? { ...t, attachments: [...(t.attachments || []), ...attachments] }
            : t
        ),
        selectedTask: state.selectedTask?.id === taskId
          ? { ...state.selectedTask, attachments: [...(state.selectedTask.attachments || []), ...attachments] }
          : state.selectedTask,
      }));

      get().addToast({ type: 'success', message: `${files.length} dosya yüklendi` });
    } catch (error: any) {
      get().addToast({ type: 'error', message: error.message });
      throw error;
    }
  },

  deleteAttachment: async (taskId: number, attachmentId: number) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/tasks/${taskId}/attachments/${attachmentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Dosya silinemedi');
      }

      // Remove attachment from task
      set(state => ({
        tasks: state.tasks.map(t => 
          t.id === taskId 
            ? { ...t, attachments: (t.attachments || []).filter(a => a.id !== attachmentId) }
            : t
        ),
        selectedTask: state.selectedTask?.id === taskId
          ? { ...state.selectedTask, attachments: (state.selectedTask.attachments || []).filter(a => a.id !== attachmentId) }
          : state.selectedTask,
      }));

      get().addToast({ type: 'success', message: 'Dosya silindi' });
    } catch (error: any) {
      get().addToast({ type: 'error', message: error.message });
      throw error;
    }
  },
}));
